import { CheckCircle2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { AuthLayout } from "../../components/AppShell";
import { Alert, Button, Field, Input } from "../../components/ui";
import { authClient } from "../../lib/auth-client";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error: err } = await authClient.requestPasswordReset({ email, redirectTo: `${window.location.origin}/reset-password` });
    setBusy(false);
    if (err && err.status === 429) return setError("Too many requests. Please wait a few minutes and try again.");
    // Always show the same confirmation, whether or not the account exists.
    setSent(true);
  }

  return (
    <AuthLayout title="Reset your password" subtitle="We'll email you a secure link to choose a new password.">
      {sent ? (
        <div className="space-y-5">
          <Alert tone="success" title="Check your email">
            If an active Galeway account uses {email}, a reset link is on its way. It expires in 30 minutes.
          </Alert>
          <Link to="/login" className="block text-center text-sm font-medium text-accent hover:underline">
            Back to sign in
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5">
          {error && <Alert tone="danger">{error}</Alert>}
          <Field label="Work email">
            {(p) => (
              <Input {...p} type="email" required autoFocus autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" />
            )}
          </Field>
          <Button type="submit" size="lg" className="w-full" loading={busy} disabled={!email}>
            Send reset link
          </Button>
          <Link to="/login" className="block text-center text-[13px] text-muted hover:text-fg">
            Back to sign in
          </Link>
        </form>
      )}
    </AuthLayout>
  );
}

export function PasswordRules({ password }: { password: string }) {
  const ok = password.length >= 12;
  return (
    <p className={`flex items-center gap-1.5 text-xs ${ok ? "text-success" : "text-subtle"}`}>
      <CheckCircle2 className="size-3.5" aria-hidden /> At least 12 characters. A memorable passphrase works well.
    </p>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(params.get("error") ? "This reset link is invalid or has expired." : null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (password !== confirm) return setError("The passwords don't match.");
    setBusy(true);
    setError(null);
    const { error: err } = await authClient.resetPassword({ newPassword: password, token: token ?? "" });
    setBusy(false);
    if (err)
      return setError(err.code === "PASSWORD_TOO_SHORT" ? "Use at least 12 characters." : "This reset link is invalid or has expired. Request a new one.");
    navigate("/login?reset=1", { replace: true });
  }

  if (!token) {
    return (
      <AuthLayout title="Link expired">
        <Alert tone="danger">This reset link is invalid or has expired.</Alert>
        <Link to="/forgot-password" className="mt-5 block text-center text-sm font-medium text-accent hover:underline">
          Request a new link
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Choose a new password" subtitle="Signing in again will be required on all your devices.">
      <form onSubmit={submit} className="space-y-5">
        {error && <Alert tone="danger">{error}</Alert>}
        <Field label="New password">
          {(p) => (
            <Input
              {...p}
              type="password"
              required
              autoFocus
              autoComplete="new-password"
              minLength={12}
              maxLength={128}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-11"
            />
          )}
        </Field>
        <PasswordRules password={password} />
        <Field label="Confirm new password">
          {(p) => (
            <Input {...p} type="password" required autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-11" />
          )}
        </Field>
        <Button type="submit" size="lg" className="w-full" loading={busy} disabled={password.length < 12 || !confirm}>
          Update password
        </Button>
      </form>
    </AuthLayout>
  );
}
